<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯自動貼標規則' : '新增自動貼標規則' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">取消</button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          {{ loading ? '處理中...' : (isEdit ? '更新' : '建立') }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <!-- 基本資訊 -->
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-group">
            <label class="required">規則名稱</label>
            <input
              v-model="formData.ruleName"
              type="text"
              class="form-control"
              placeholder="請輸入規則名稱"
              maxlength="100"
              required
            />
          </div>

          <div class="form-group">
            <label>規則說明</label>
            <textarea
              v-model="formData.ruleDescription"
              class="form-control"
              rows="3"
              placeholder="請輸入規則說明"
            ></textarea>
          </div>

          <div class="form-group">
            <label>是否啟用</label>
            <label class="switch">
              <input type="checkbox" v-model="formData.isActive" />
              <span class="slider"></span>
            </label>
          </div>
        </div>

        <!-- 標籤設定 -->
        <div class="form-section">
          <h3 class="section-title">標籤設定</h3>
          
          <div class="form-group">
            <label class="required">標籤名稱</label>
            <TagSelector
              v-model="formData.tagName"
              placeholder="請選擇或新增自動規則標籤"
              :allow-create="true"
              tag-source="auto_rule"
              @tag-selected="handleTagSelected"
            />
            <small class="form-text">只能使用自動規則標籤。打開後可選擇現有標籤，或輸入新名稱再按「新增自動規則標籤」。顏色與圖示會沿用該標籤。</small>
          </div>
        </div>

        <!-- 條件設定 -->
        <div class="form-section">
          <h3 class="section-title">條件設定</h3>
          
          <MemberConditionGroups ref="conditionEditor" v-model="formData.conditionGroups" />
        </div>

        <!-- 執行設定 -->
        <div class="form-section">
          <h3 class="section-title">執行設定</h3>
          
          <!-- 執行設定說明 -->
          <div class="info-box">
            <div class="info-box-icon">
              <i class="ri-information-line"></i>
            </div>
            <div class="info-box-content">
              <p class="info-box-text">
                <strong>「自動執行」說明：</strong><br>
                勾選「自動執行」後，系統會在下方勾選的<strong>時機自動執行此規則</strong>。<br>
                您必須至少勾選一個觸發時機（註冊時/消費後/每日批次），規則才會真正執行。
              </p>
            </div>
          </div>
          
          <!-- 智能建議區塊 -->
          <div v-if="executionSuggestions.length > 0" class="suggestion-box">
            <div class="suggestion-icon">
              <i class="ri-lightbulb-line"></i>
            </div>
            <div class="suggestion-content">
              <p class="suggestion-title">💡 建議的執行設定</p>
              <ul class="suggestion-list">
                <li v-for="(suggestion, index) in executionSuggestions" :key="index">
                  {{ suggestion }}
                </li>
              </ul>
              <button type="button" class="btn btn-sm btn-primary" @click="applyRecommendedSettings">
                <i class="ri-check-line"></i>
                套用建議設定
              </button>
            </div>
          </div>
          
          <!-- 警告區塊 -->
          <div v-if="executionWarnings.length > 0" class="warning-box">
            <div class="warning-icon">
              <i class="ri-error-warning-line"></i>
            </div>
            <div class="warning-content">
              <p class="warning-title">⚠️ 執行設定警告</p>
              <ul class="warning-list">
                <li v-for="(warning, index) in executionWarnings" :key="index">
                  {{ warning }}
                </li>
              </ul>
            </div>
          </div>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.autoExecute" @change="handleAutoExecuteChange" />
              啟用自動執行
            </label>
            <small class="form-text">關閉後，規則只能透過「手動執行」按鈕觸發</small>
          </div>

          <div v-if="formData.autoExecute" class="execution-triggers">
            <p class="triggers-label">請選擇自動執行的觸發時機：</p>
            
            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.executeOnRegistration" @change="handleExecutionChange" />
                <span class="trigger-name">會員註冊時執行</span>
              </label>
              <small class="form-text">
                <i class="ri-time-line"></i> 當新會員註冊時，<strong>立即</strong>執行此規則<br>
                適用於：基本資料類條件（性別、生日、年齡等）
              </small>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.executeOnPurchase" @change="handleExecutionChange" />
                <span class="trigger-name">會員消費後執行</span>
              </label>
              <small class="form-text">
                <i class="ri-time-line"></i> 當會員完成消費後，<strong>立即</strong>執行此規則<br>
                適用於：消費相關條件（消費次數、金額、最後消費天數等）
              </small>
            </div>

            <div class="form-group">
              <label class="checkbox-label">
                <input type="checkbox" v-model="formData.executeDaily" @change="handleExecutionChange" />
                <span class="trigger-name">每日批次執行</span>
              </label>
              <small class="form-text">
                <i class="ri-time-line"></i> 系統每日<strong>凌晨 1 點</strong>自動執行此規則<br>
                適用於：所有類型的條件（建議至少勾選此項）
              </small>
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>

  </div>
</template>

<script>
import { ref, reactive, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { autoTagRuleService } from '@/api/modules/autoTagRules';
import { tagService } from '@/api/modules/tags';
import { showSuccess, showError } from '@/utils/swal.js';
import TagSelector from '@/components/TagSelector.vue';
import MemberConditionGroups from '@/components/MemberConditionGroups.vue';
import { hydrateConditionGroups } from '@/utils/memberConditions';

export default {
  name: 'AutoTagRuleForm',
  components: {
    TagSelector,
    MemberConditionGroups
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const conditionEditor = ref(null);

    // 智能提示相關狀態
    const executionSuggestions = ref([]);
    const executionWarnings = ref([]);
    const recommendedSettings = ref(null);
    const hasUserModifiedExecutionSettings = ref(false);

    const isEdit = computed(() => !!route.params.id);

    // 表單資料
    const formData = reactive({
      ruleName: '',
      ruleDescription: '',
      isActive: true,
      tagName: '',
      tagColor: null, // 從選中的標籤自動取得
      tagIcon: null, // 從選中的標籤自動取得
      autoExecute: true,
      executeOnRegistration: true,
      executeOnPurchase: true,
      executeDaily: true,
      conditionGroups: [
        {
          logicalOperator: 'AND',
          conditions: [
            {
              id: `cond_${Date.now()}`,
              conditionType: '',
              operator: '',
              value: ''
            }
          ]
        }
      ]
    });

    // 處理標籤選擇
    const handleTagSelected = (tag) => {
      if (tag) {
        // 如果選擇了現有標籤，自動取得顏色和圖示
        formData.tagColor = tag.tagColor || null;
        formData.tagIcon = tag.tagIcon || null;
      } else {
        // 如果是新標籤或清除選擇，顏色和圖示為 null
        formData.tagColor = null;
        formData.tagIcon = null;
      }
    };

    // 監聽標籤名稱變化，如果直接輸入（非選擇），清除顏色和圖示
    watch(() => formData.tagName, async (newTagName) => {
      if (newTagName && newTagName.trim()) {
        // 嘗試從標籤列表中查找，如果找不到，表示是新標籤
        try {
          const response = await tagService.getTags({ isActive: true, tagSource: 'auto_rule' });
          const tags = response.data || [];
          const foundTag = tags.find(tag => tag.tagName === newTagName.trim());
          if (foundTag) {
            // 找到現有標籤，自動取得顏色和圖示
            formData.tagColor = foundTag.tagColor || null;
            formData.tagIcon = foundTag.tagIcon || null;
          } else {
            // 新標籤，顏色和圖示為 null
            formData.tagColor = null;
            formData.tagIcon = null;
          }
        } catch (error) {
          // 載入失敗，設為 null
          formData.tagColor = null;
          formData.tagIcon = null;
        }
      } else {
        // 標籤名稱為空，清除顏色和圖示
        formData.tagColor = null;
        formData.tagIcon = null;
      }
    });

    // 監聽條件組變化，自動驗證執行設定
    watch(() => formData.conditionGroups, () => {
      if (!hasUserModifiedExecutionSettings.value) {
        validateExecutionSettings();
      }
    }, { deep: true });

    // 載入規則詳情（編輯模式）
    const loadRuleDetail = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        const response = await autoTagRuleService.getAutoTagRule(route.params.id);
        const rule = response.data;

        hydrateConditionGroups(rule.conditionGroups);

        Object.assign(formData, {
          ruleName: rule.ruleName,
          ruleDescription: rule.ruleDescription,
          isActive: rule.isActive,
          tagName: rule.tagName,
          tagColor: rule.tagColor || null,
          tagIcon: rule.tagIcon || null,
          autoExecute: rule.autoExecute,
          executeOnRegistration: rule.executeOnRegistration,
          executeOnPurchase: rule.executeOnPurchase,
          executeDaily: rule.executeDaily,
          conditionGroups: rule.conditionGroups || formData.conditionGroups
        });
      } catch (error) {
        console.error('載入規則詳情失敗:', error);
        await showError('載入失敗', '載入規則詳情失敗');
        handleBack();
      } finally {
        loading.value = false;
      }
    };

    /**
     * 取得所有條件類型
     */
    const getConditionTypes = () => {
      const types = [];
      for (const group of formData.conditionGroups) {
        for (const condition of group.conditions) {
          if (condition.conditionType) {
            types.push(condition.conditionType);
          }
        }
      }
      return [...new Set(types)]; // 去重
    };

    /**
     * 驗證執行設定並提供建議
     */
    const validateExecutionSettings = () => {
      executionSuggestions.value = [];
      executionWarnings.value = [];
      recommendedSettings.value = null;
      
      // 分析條件類型
      const conditionTypes = getConditionTypes();
      
      // 如果沒有條件，不提供建議
      if (conditionTypes.length === 0 || !conditionTypes[0]) {
        return;
      }
      
      // 檢查是否有消費相關條件
      const hasPurchaseConditions = conditionTypes.some(type => 
        ['total_purchase_count', 'total_purchase_amount', 'last_purchase_days', 'purchased_category', 'purchased_product', 'purchased_product_name', 'purchase_count_within_days', 'purchase_amount_within_days'].includes(type)
      );
      
      // 檢查是否有註冊天數條件
      const hasRegistrationDays = conditionTypes.includes('registration_days');
      
      // 檢查是否有基本資料條件
      const hasProfileConditions = conditionTypes.some(type => 
        ['age', 'gender', 'birth_month'].includes(type)
      );
      
      // 檢查是否有標籤條件
      const hasTagConditions = conditionTypes.includes('tag_exists');
      
      // 生成建議
      if (hasPurchaseConditions && !hasRegistrationDays && !hasProfileConditions) {
        // 純消費相關條件
        recommendedSettings.value = {
          autoExecute: true,
          executeOnRegistration: false,
          executeOnPurchase: true,
          executeDaily: true
        };
        
        executionSuggestions.value.push(
          '偵測到消費相關條件（消費次數、金額、最後消費天數）',
          '建議勾選：「會員消費後執行」+ 「每日批次執行」',
          '原因：會員剛註冊時消費記錄為空，無法觸發規則'
        );
        
        // 檢查是否錯誤勾選註冊時執行
        if (formData.executeOnRegistration && !formData.executeOnPurchase && !formData.executeDaily) {
          executionWarnings.value.push(
            '⚠️ 僅勾選「會員註冊時執行」，但條件包含消費相關欄位',
            '會員剛註冊時消費記錄必定為空（次數=0、金額=0），規則永遠無法觸發',
            '請改勾選「會員消費後執行」或「每日批次執行」'
          );
        }
      } else if (hasRegistrationDays && hasPurchaseConditions) {
        // 註冊天數 + 消費條件
        recommendedSettings.value = {
          autoExecute: true,
          executeOnRegistration: false,
          executeOnPurchase: true,
          executeDaily: true
        };
        
        executionSuggestions.value.push(
          '同時包含「註冊天數」和「消費相關條件」',
          '建議勾選：「會員消費後執行」+ 「每日批次執行」',
          '原因：需要在會員消費後才能檢查條件是否符合'
        );
      } else if (hasRegistrationDays && !hasPurchaseConditions) {
        // 只有註冊天數
        recommendedSettings.value = {
          autoExecute: true,
          executeOnRegistration: true,
          executeOnPurchase: false,
          executeDaily: true
        };
        
        executionSuggestions.value.push(
          '偵測到「註冊天數」條件',
          '建議勾選：「會員註冊時執行」+ 「每日批次執行」',
          '原因：可在註冊時立即標記，並每日更新（移除過期標籤）'
        );
      } else if (hasProfileConditions && !hasPurchaseConditions) {
        // 只有基本資料條件
        recommendedSettings.value = {
          autoExecute: true,
          executeOnRegistration: true,
          executeOnPurchase: false,
          executeDaily: true
        };
        
        executionSuggestions.value.push(
          '偵測到基本資料條件（性別、年齡、生日月份）',
          '建議勾選：「會員註冊時執行」+ 「每日批次執行」',
          '原因：基本資料在註冊時就已確定，可立即標記'
        );
      } else if (hasTagConditions) {
        // 有標籤條件
        recommendedSettings.value = {
          autoExecute: true,
          executeOnRegistration: false,
          executeOnPurchase: true,
          executeDaily: true
        };
        
        executionSuggestions.value.push(
          '偵測到「標籤存在」條件',
          '建議勾選：「會員消費後執行」+ 「每日批次執行」',
          '原因：會員剛註冊時通常沒有標籤'
        );
      }
      
      // 檢查是否全部未勾選
      if (formData.autoExecute && 
          !formData.executeOnRegistration && 
          !formData.executeOnPurchase && 
          !formData.executeDaily) {
        executionWarnings.value.push(
          '❌ 已啟用「自動執行」但未勾選任何觸發時機',
          '規則將永遠不會自動執行，等於規則形同虛設',
          '請至少勾選一個觸發時機'
        );
      }
      
      // 檢查是否只勾選每日批次
      if (!formData.executeOnRegistration && 
          !formData.executeOnPurchase && 
          formData.executeDaily && 
          (hasPurchaseConditions || hasRegistrationDays)) {
        executionWarnings.value.push(
          '⚠️ 僅勾選「每日批次執行」（凌晨 1 點）',
          '會員需等到隔天凌晨才會被貼上標籤，無法即時反應',
          '建議額外勾選「會員消費後執行」，以提供即時標記'
        );
      }
      
      // 檢查是否關閉自動執行
      if (!formData.autoExecute) {
        executionWarnings.value.push(
          'ℹ️ 已關閉「自動執行」',
          '規則只能透過列表頁的「手動執行」按鈕觸發',
          '適用於：需要人工審核後才執行的規則'
        );
      }
    };

    /**
     * 套用建議設定
     */
    const applyRecommendedSettings = () => {
      if (recommendedSettings.value) {
        formData.autoExecute = recommendedSettings.value.autoExecute;
        formData.executeOnRegistration = recommendedSettings.value.executeOnRegistration;
        formData.executeOnPurchase = recommendedSettings.value.executeOnPurchase;
        formData.executeDaily = recommendedSettings.value.executeDaily;
        
        hasUserModifiedExecutionSettings.value = true;
        validateExecutionSettings();
      }
    };

    /**
     * 處理自動執行變更
     */
    const handleAutoExecuteChange = () => {
      hasUserModifiedExecutionSettings.value = true;
      validateExecutionSettings();
    };

    /**
     * 處理觸發時機變更
     */
    const handleExecutionChange = () => {
      hasUserModifiedExecutionSettings.value = true;
      validateExecutionSettings();
    };

    // 提交表單
    const handleSubmit = async () => {
      try {
        // 驗證基本資訊
        if (!formData.ruleName || !formData.ruleName.trim()) {
          await showError('驗證失敗', '請輸入規則名稱');
          return;
        }
        if (!formData.tagName || !formData.tagName.trim()) {
          await showError('驗證失敗', '請選擇或輸入標籤名稱');
          return;
        }

        const conditionError = conditionEditor.value?.validate();
        if (conditionError) {
          await showError('驗證失敗', conditionError);
          return;
        }

        loading.value = true;

        const payload = { ...formData };
        if (!/^#[0-9A-Fa-f]{6}$/.test(payload.tagColor || '')) {
          delete payload.tagColor;
        }
        if (!payload.tagIcon) {
          delete payload.tagIcon;
        }

        if (isEdit.value) {
          await autoTagRuleService.updateAutoTagRule(route.params.id, payload);
          await showSuccess('更新成功', '');
        } else {
          await autoTagRuleService.createAutoTagRule(payload);
          await showSuccess('建立成功', '');
        }

        handleBack();
      } catch (error) {
        console.error('提交失敗:', error);
        await showError('提交失敗', error.message || (isEdit.value ? '更新失敗' : '建立失敗'));
      } finally {
        loading.value = false;
      }
    };

    // 返回列表頁
    const handleBack = () => {
      router.push('/client/auto-tag-rules');
    };

    onMounted(() => {
      loadRuleDetail();
    });

    return {
      loading,
      isEdit,
      conditionEditor,
      executionSuggestions,
      executionWarnings,
      formData,
      loadRuleDetail,
      handleTagSelected,
      validateExecutionSettings,
      applyRecommendedSettings,
      handleAutoExecuteChange,
      handleExecutionChange,
      handleSubmit,
      handleBack
    };
  }
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left {
  display: flex;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.btn {
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }

.form-container {
  max-width: 900px;
}

.form-section {
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e4e7ed;
}

.form-section:last-child {
  border-bottom: none;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #303133;
}

/* 執行設定說明框 */
.info-box {
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  margin-bottom: 20px;
  background: #e6f7ff;
  border-left: 4px solid #1890ff;
  border-radius: 4px;
}

.info-box-icon {
  flex-shrink: 0;
  font-size: 20px;
  color: #1890ff;
}

.info-box-content {
  flex: 1;
}

.info-box-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #00476a;
}

/* 建議區塊 */
.suggestion-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
  border: 2px solid #52c41a;
  border-radius: 8px;
}

.suggestion-icon {
  flex-shrink: 0;
  font-size: 24px;
  color: #52c41a;
}

.suggestion-content {
  flex: 1;
}

.suggestion-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #237804;
}

.suggestion-list {
  margin: 0 0 12px 0;
  padding-left: 20px;
  list-style: none;
}

.suggestion-list li {
  position: relative;
  margin-bottom: 8px;
  padding-left: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #389e0d;
}

.suggestion-list li:before {
  content: "✓";
  position: absolute;
  left: -12px;
  font-weight: bold;
  color: #52c41a;
}

/* 警告區塊 */
.warning-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
  border: 2px solid #fa8c16;
  border-radius: 8px;
  animation: shake 0.5s ease;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.warning-icon {
  flex-shrink: 0;
  font-size: 24px;
  color: #fa8c16;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

.warning-content {
  flex: 1;
}

.warning-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #ad4e00;
}

.warning-list {
  margin: 0;
  padding-left: 20px;
  list-style: none;
}

.warning-list li {
  position: relative;
  margin-bottom: 8px;
  padding-left: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #d46b08;
}

.warning-list li:before {
  content: "!";
  position: absolute;
  left: -12px;
  font-weight: bold;
  color: #fa8c16;
}

/* 執行觸發選項 */
.execution-triggers {
  margin-left: 24px;
  padding: 16px;
  background: #f5f7fa;
  border-radius: 4px;
}

.triggers-label {
  font-weight: 600;
  margin-bottom: 12px;
  color: #303133;
}

.trigger-name {
  font-weight: 500;
}


.form-group {
  margin-bottom: 16px;
}

.form-group label.required::after {
  content: " *";
  color: #f56c6c;
}

.form-text {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}

.radio-group {
  display: flex;
  gap: 20px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

/* 邏輯說明區塊（可折疊） */
.logic-info-box {
  margin-bottom: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;
  overflow: hidden;
  transition: all 0.3s ease;
}

.logic-info-box.collapsed {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border: 2px solid #667eea;
}

.logic-info-box.collapsed .info-header {
  color: #667eea;
}

.info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  transition: background 0.2s ease;
}

.info-header:hover {
  background: rgba(255, 255, 255, 0.1);
}

.logic-info-box.collapsed .info-header:hover {
  background: rgba(102, 126, 234, 0.05);
}

.info-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-header-left i {
  font-size: 20px;
}

.expand-icon {
  font-size: 20px;
  transition: transform 0.3s ease;
}

.info-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

.info-content {
  padding: 0 16px 16px 16px;
}

.info-list {
  margin: 0 0 12px 0;
  padding-left: 20px;
}

.info-list li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.info-list ul {
  margin-top: 4px;
  padding-left: 20px;
}

.info-list ul li {
  margin-bottom: 4px;
  font-size: 13px;
  opacity: 0.95;
}

.info-example {
  font-size: 13px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  margin: 0;
  line-height: 1.8;
}

.result-text {
  display: block;
  margin-top: 8px;
  font-weight: 600;
}

/* 折疊動畫 */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  transform: translateY(-10px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
}

.badge-and {
  background: rgba(103, 194, 58, 0.9);
  color: white;
}

.badge-or {
  background: rgba(245, 108, 108, 0.9);
  color: white;
}

.condition-group {
  padding: 16px;
  margin-bottom: 16px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-weight: 600;
}

.condition-item {
  margin-bottom: 12px;
}

.condition-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.flex-1 {
  flex: 1;
}

.flex-2 {
  flex: 2;
}

/* 購買品類／購買商品條件的值編輯區 */
.purchase-condition {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.purchase-params {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.purchase-param {
  display: flex;
  align-items: center;
  gap: 6px;
}

.param-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

.param-input {
  width: 90px;
}

.product-pick-btn {
  justify-content: flex-start;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .condition-row {
    flex-direction: column;
    align-items: stretch;
  }
}

/* Switch 樣式 */
.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: .4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #667eea;
}

input:checked + .slider:before {
  transform: translateX(20px);
}
</style>
